<script setup>
/**
 * One number from the Activity screen, with the same number from the period before it.
 *
 * ANATOMY IS `StatTile`'s, deliberately — `CardLayout` unchanged (`px-6 py-5`,
 * `rounded-xl bg-n-solid-2`, `outline outline-1 outline-n-container`, no shadow), the same
 * `gap-1` internal rhythm, the same type sizes. The two screens show the same KIND of
 * thing and must not look like two products. What this adds, and the only reason it is a
 * second component rather than a prop on the first, is the comparison line: Overview
 * answers "is my assistant healthy right now" and has nothing to compare against, while a
 * report is a claim about a PERIOD and a period is meaningless without the one before it.
 *
 * THE DIRECTION MARK CARRIES NO JUDGEMENT, and that is a decision, not an omission.
 * Chatwoot's own reports paint deltas green and red; this one does not, because on this
 * page it would be a lie in two directions at once. More conversations is good news for a
 * shop and bad news for a shop drowning in them; a higher handover rate means the
 * assistant is coping less well, OR that the owner turned on a handover rule on purpose
 * this week. Nothing here knows which. So the arrow says "up" and "down" — a fact — and
 * the colour stays `n-slate-11` for both. It is also why the accent never appears on this
 * card: green here would read as "this number is good", which is the same class of
 * unearned reassurance as a fabricated zero.
 *
 * `value` and `compare` arrive PRE-FORMATTED, in the account's locale. A tile that built
 * its own percentage would render `21%` for an account that writes `٢١٪`.
 */
import CardLayout from 'dashboard/components-next/CardLayout.vue';

defineProps({
  label: { type: String, required: true },
  /** Pre-formatted. Empty renders nothing — never a fabricated zero. */
  value: { type: String, default: '' },
  /** The supporting fact under the number. Optional. */
  hint: { type: String, default: '' },
  /** The same number, one period earlier, as a whole sentence. Optional. */
  compare: { type: String, default: '' },
  /** `up` | `down` | `same` | '' — direction only, no verdict. */
  direction: {
    type: String,
    default: '',
    validator: value => ['', 'up', 'down', 'same'].includes(value),
  },
});

const ARROWS = {
  up: 'i-lucide-arrow-up',
  down: 'i-lucide-arrow-down',
  same: 'i-lucide-minus',
};
</script>

<template>
  <CardLayout>
    <div class="flex flex-col gap-1">
      <p class="mb-0 text-sm text-n-slate-11">{{ label }}</p>
      <p
        class="mb-0 text-3xl font-medium leading-9 text-n-slate-12"
        data-waslo-metric-value
      >
        {{ value }}
      </p>
      <p v-if="hint" class="mb-0 text-sm text-n-slate-10">{{ hint }}</p>
      <p
        v-if="compare"
        class="flex items-center gap-1 mt-1 mb-0 text-sm text-n-slate-11"
        data-waslo-metric-compare
      >
        <!-- Direction, not judgement. See the block comment. -->
        <span
          v-if="ARROWS[direction]"
          class="shrink-0 size-3.5"
          :class="ARROWS[direction]"
          aria-hidden="true"
        />
        <span class="min-w-0">{{ compare }}</span>
      </p>
    </div>
  </CardLayout>
</template>
